import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Plus, X, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  useWebhook,
  useCreateWebhook,
  useUpdateWebhook,
} from "@/hooks/use-webhooks"

const EVENT_LABELS: Record<string, string> = {
  deal_created: "Nowy deal",
  deal_updated: "Aktualizacja deala",
  deal_phase_changed: "Zmiana fazy deala",
  deal_won: "Deal wygrany",
  deal_lost: "Deal przegrany",
  task_created: "Nowe zadanie",
  task_completed: "Zadanie ukończone",
  contact_created: "Nowy kontakt",
  company_created: "Nowa firma",
  service_ticket_created: "Nowe zgłoszenie",
  service_ticket_phase_changed: "Zmiana fazy zgłoszenia",
  service_ticket_resolved: "Zgłoszenie rozwiązane",
  invoice_issued: "Faktura wystawiona",
  invoice_paid: "Faktura opłacona",
  quote_accepted: "Oferta zaakceptowana",
  quote_rejected: "Oferta odrzucona",
}

const EVENT_GROUPS: { label: string; events: string[] }[] = [
  {
    label: "Deale",
    events: ["deal_created", "deal_updated", "deal_phase_changed", "deal_won", "deal_lost"],
  },
  { label: "Zadania", events: ["task_created", "task_completed"] },
  { label: "Kontakty", events: ["contact_created", "company_created"] },
  {
    label: "Serwis",
    events: [
      "service_ticket_created",
      "service_ticket_phase_changed",
      "service_ticket_resolved",
    ],
  },
  { label: "Dokumenty", events: ["invoice_issued", "invoice_paid", "quote_accepted", "quote_rejected"] },
]

export function WebhookFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const { data: webhookData } = useWebhook(id ?? "")
  const createMutation = useCreateWebhook()
  const updateMutation = useUpdateWebhook()

  const [name, setName] = useState("")
  const [url, setUrl] = useState("")
  const [secret, setSecret] = useState("")
  const [showSecret, setShowSecret] = useState(false)
  const [description, setDescription] = useState("")
  const [isActive, setIsActive] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([])

  useEffect(() => {
    if (isEdit && webhookData?.data) {
      const w = webhookData.data
      setName(w.name)
      setUrl(w.url)
      setDescription(w.description ?? "")
      setIsActive(w.is_active)
      setSelectedEvents(w.subscribed_events)
      const h = Object.entries(w.headers || {}).map(([key, value]) => ({ key, value }))
      setHeaders(h.length > 0 ? h : [])
    }
  }, [isEdit, webhookData])

  const toggleEvent = (event: string) => {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    )
  }

  const selectAllInGroup = (events: string[]) => {
    const allSelected = events.every((e) => selectedEvents.includes(e))
    if (allSelected) {
      setSelectedEvents((prev) => prev.filter((e) => !events.includes(e)))
    } else {
      setSelectedEvents((prev) => [...new Set([...prev, ...events])])
    }
  }

  const addHeader = () => setHeaders((prev) => [...prev, { key: "", value: "" }])
  const removeHeader = (idx: number) => setHeaders((prev) => prev.filter((_, i) => i !== idx))
  const updateHeader = (idx: number, field: "key" | "value", val: string) =>
    setHeaders((prev) => prev.map((h, i) => (i === idx ? { ...h, [field]: val } : h)))

  const handleSubmit = () => {
    if (!name.trim()) return toast.error("Podaj nazwę")
    if (!url.trim()) return toast.error("Podaj URL")
    if (selectedEvents.length === 0) return toast.error("Wybierz przynajmniej jedno zdarzenie")

    const headersObj: Record<string, string> = {}
    headers.forEach((h) => {
      if (h.key.trim()) headersObj[h.key.trim()] = h.value
    })

    const payload: Record<string, unknown> = {
      name: name.trim(),
      url: url.trim(),
      description: description.trim() || null,
      is_active: isActive,
      subscribed_events: selectedEvents,
      headers: headersObj,
    }

    // Only include secret if it's been changed (not the masked ***)
    if (secret && secret !== "***") {
      payload.secret = secret
    }

    if (isEdit && id) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            toast.success("Webhook zaktualizowany")
            navigate(`/webhooks/${id}`)
          },
          onError: (e) => toast.error(e instanceof Error ? e.message : "Błąd"),
        }
      )
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          toast.success("Webhook utworzony")
          navigate(`/webhooks/${res.data.id}`)
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Błąd"),
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(isEdit ? `/webhooks/${id}` : "/webhooks")}
          className="p-2 rounded-lg hover:opacity-80 text-muted-foreground"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-semibold text-foreground">
          {isEdit ? "Edytuj webhook" : "Nowy webhook"}
        </h1>
      </div>

      {/* Name & URL */}
      <div className="rounded-lg border p-5 space-y-4 border-border bg-card">
        <h2 className="text-sm font-medium text-foreground">
          Podstawowe informacje
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              Nazwa *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Slack Notification"
              className="w-full px-3 py-2 rounded-lg border text-sm border-border bg-background text-foreground"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/webhook"
              className="w-full px-3 py-2 rounded-lg border text-sm font-mono border-border bg-background text-foreground"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              Opis
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Opcjonalny opis webhooka..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none border-border bg-background text-foreground"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              Secret (HMAC-SHA256)
            </label>
            <div className="relative">
              <input
                type={showSecret ? "text" : "password"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Opcjonalny klucz do podpisywania payloadu"
                className="w-full px-3 py-2 pr-10 rounded-lg border text-sm font-mono border-border bg-background text-foreground"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground"
              >
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">
              Payload będzie podpisany nagłówkiem X-Webhook-Signature
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative w-10 h-5 rounded-full transition-colors ${isActive ? "bg-primary" : "bg-muted"}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  isActive ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm text-foreground">
              {isActive ? "Aktywny" : "Nieaktywny"}
            </span>
          </div>
        </div>
      </div>

      {/* Events */}
      <div className="rounded-lg border p-5 space-y-4 border-border bg-card">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-foreground">
            Subskrybowane zdarzenia *
          </h2>
          <span className="text-xs text-muted-foreground">
            {selectedEvents.length} wybranych
          </span>
        </div>
        <div className="space-y-4">
          {EVENT_GROUPS.map((group) => {
            const allSelected = group.events.every((e) => selectedEvents.includes(e))
            const someSelected = group.events.some((e) => selectedEvents.includes(e))
            return (
              <div key={group.label}>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => selectAllInGroup(group.events)}
                    className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                      allSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : someSelected
                          ? "border-primary text-primary"
                          : "border-border"
                    }`}
                  >
                    {allSelected ? "✓" : someSelected ? "—" : ""}
                  </button>
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    {group.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pl-6">
                  {group.events.map((event) => (
                    <label
                      key={event}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        selectedEvents.includes(event)
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(event)}
                        onChange={() => toggleEvent(event)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center text-xs shrink-0 text-primary-foreground ${
                          selectedEvents.includes(event)
                            ? "border-primary bg-primary"
                            : "border-border"
                        }`}
                      >
                        {selectedEvents.includes(event) && "✓"}
                      </div>
                      <span className="text-sm text-foreground">
                        {EVENT_LABELS[event] ?? event}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Custom Headers */}
      <div className="rounded-lg border p-5 space-y-4 border-border bg-card">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-foreground">
            Dodatkowe nagłówki HTTP
          </h2>
          <button
            type="button"
            onClick={addHeader}
            className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded text-primary"
          >
            <Plus size={14} />
            Dodaj
          </button>
        </div>
        {headers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Brak dodatkowych nagłówków
          </p>
        ) : (
          <div className="space-y-2">
            {headers.map((h, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={h.key}
                  onChange={(e) => updateHeader(idx, "key", e.target.value)}
                  placeholder="Header-Name"
                  className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono border-border bg-background text-foreground"
                />
                <input
                  type="text"
                  value={h.value}
                  onChange={(e) => updateHeader(idx, "value", e.target.value)}
                  placeholder="value"
                  className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono border-border bg-background text-foreground"
                />
                <button
                  type="button"
                  onClick={() => removeHeader(idx)}
                  className="p-2 rounded hover:opacity-80 text-muted-foreground"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 justify-end">
        <Button
          variant="ghost"
          onClick={() => navigate(isEdit ? `/webhooks/${id}` : "/webhooks")}
        >
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isPending}
        >
          {isPending ? "Zapisywanie..." : isEdit ? "Zapisz" : "Utwórz webhook"}
        </Button>
      </div>
    </div>
  )
}
